import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { FlagDefinition } from '@/API';
import { FlagDefinitionService } from '@/service/FlagDefinitionService';
import { houseListFilters } from '@/pages/HouseListFilterPage';
import { boughtListFilters } from '@/pages/BoughtListFilterPage';
import { useServices } from '@/components/ServiceContext';
import { CountMapUtils } from '@/utils/CountMapUtils';
import FlagDefinitionCard from './FlagDefinitionCard';
import log from 'loglevel';

interface FieldOption {
  label: string;
  fieldName: string;
  listType: string;
  displayName: string; // Combines list type and field name for display
}

const FlagDictionaryManager: React.FC = () => {
  const [flagService] = useState(() => new FlagDefinitionService());
  const { houseListService, boughtListService } = useServices();

  const [selectedField, setSelectedField] = useState<FieldOption | null>(null);
  const [flagDefinitions, setFlagDefinitions] = useState<FlagDefinition[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchingFlags, setFetchingFlags] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Build field options once (filters are module constants)
  const arrayFields: FieldOption[] = useMemo(
    () =>
      [
        ...houseListFilters
          .filter(filter => filter.isArrayField && filter.label)
          .map(filter => ({
            label: filter.label!,
            fieldName: filter.databaseMetadata?.columnName || '',
            listType: 'houselist',
            displayName: `House List - ${filter.label}`,
          })),
        ...boughtListFilters
          .filter(filter => filter.isArrayField && filter.label)
          .map(filter => ({
            label: filter.label!,
            fieldName: filter.databaseMetadata?.columnName || '',
            listType: 'boughtlist',
            displayName: `Bought List - ${filter.label}`,
          })),
      ].filter(field => field.fieldName),
    []
  );

  const loadFlagDefinitions = useCallback(
    async (field: FieldOption) => {
      setLoading(true);
      setError(null);

      try {
        const definitions = await flagService.getFlagDefinitionsByField(
          field.listType,
          field.fieldName
        );

        const sortedDefinitions = definitions.sort((a, b) =>
          a.flagValue.localeCompare(b.flagValue)
        );

        setFlagDefinitions(sortedDefinitions);
      } catch (err) {
        setError('Failed to load flag definitions');
        log.error('Error loading flag definitions:', err);
      } finally {
        setLoading(false);
      }
    },
    [flagService]
  );

  const fetchAvailableFlags = useCallback(
    async (field: FieldOption): Promise<string[]> => {
      setFetchingFlags(true);
      setError(null);

      try {
        const allFilters =
          field.listType === 'houselist' ? houseListFilters : boughtListFilters;

        const filterDef = allFilters.find(
          f => f.databaseMetadata?.columnName === field.fieldName
        );

        if (!filterDef) {
          throw new Error(
            `Filter definition not found for field ${field.fieldName}`
          );
        }

        const service =
          field.listType === 'houselist' ? houseListService : boughtListService;

        const countMap = await service.getFilterCountMap(
          filterDef.id,
          allFilters,
          {} // Empty filter selections to get all values
        );

        const flagValues = Array.from(countMap.keys())
          .filter(
            (key): key is string =>
              !CountMapUtils.isTotalCountKey(key) &&
              typeof key === 'string' &&
              key.trim() !== ''
          )
          .sort();

        return flagValues;
      } catch (err) {
        setError('Failed to fetch available flags from data');
        log.error('Error fetching available flags:', err);
        return [];
      } finally {
        setFetchingFlags(false);
      }
    },
    [houseListService, boughtListService]
  );

  const createMissingDefaults = useCallback(
    async (field: FieldOption, flagsToCheck: string[]) => {
      if (flagsToCheck.length === 0) return;

      try {
        const results = await flagService.createDefaultFlagDefinitions(
          field.listType,
          field.fieldName,
          flagsToCheck
        );

        if (results.length > 0) {
          await loadFlagDefinitions(field);
        }
      } catch (err) {
        log.warn('Failed to auto-create missing defaults:', err);
        // Don't show error to user since this is automatic
      }
    },
    [flagService, loadFlagDefinitions]
  );

  const handleFieldChange = useCallback(
    async (field: FieldOption) => {
      setSelectedField(field);
      setEditingId(null);

      // Load existing flag definitions and fetch available flags from data in parallel
      const [, flags] = await Promise.all([
        loadFlagDefinitions(field),
        fetchAvailableFlags(field),
      ]);

      // Automatically create missing defaults after loading data
      await createMissingDefaults(field, flags);
    },
    [loadFlagDefinitions, fetchAvailableFlags, createMissingDefaults]
  );

  // Auto-select the first field on component mount
  useEffect(() => {
    if (arrayFields.length > 0 && !selectedField) {
      void handleFieldChange(arrayFields[0]);
    }
  }, [arrayFields, handleFieldChange, selectedField]);

  const handleUpdateFlag = async (updated: FlagDefinition) => {
    try {
      const updatedFlag = await flagService.updateFlagDefinition({
        id: updated.id,
        listType: updated.listType,
        fieldName: updated.fieldName,
        flagValue: updated.flagValue,
        friendlyName: updated.friendlyName,
        description: updated.description || undefined,
      });

      // Update the local state immediately with the updated flag
      // Use the input data if the service doesn't return the full object
      const flagToUpdate = updatedFlag || updated;

      setFlagDefinitions(prev => {
        const updatedList = prev.map(flag =>
          flag.id === flagToUpdate.id ? { ...flag, ...flagToUpdate } : flag
        );
        // Keep the list sorted by flag value
        return updatedList.sort((a, b) =>
          a.flagValue.localeCompare(b.flagValue)
        );
      });
    } catch (err) {
      log.error('Update error:', err);
      throw err; // Re-throw to let the card handle it
    }
  };

  return (
    <Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Manage user-friendly names and descriptions for flag values in array
        fields from both House List and Bought List. Select a field below to
        view and edit its flag definitions.
      </Typography>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel id="field-select-label">Select Field</InputLabel>
        <Select
          labelId="field-select-label"
          value={selectedField?.fieldName || ''}
          onChange={e => {
            const field = arrayFields.find(f => f.fieldName === e.target.value);
            if (field) {
              void handleFieldChange(field);
            }
          }}
          label="Select Field"
          displayEmpty
        >
          {arrayFields.map(field => (
            <MenuItem key={field.fieldName} value={field.fieldName}>
              {field.displayName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedField && (
        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h5">
              Flag Definitions for {selectedField.displayName}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {flagDefinitions.length === 0 ? (
                <Alert severity="info">
                  {fetchingFlags
                    ? 'Loading flag definitions...'
                    : 'No flag definitions found for this field. Flag definitions will be created automatically when flag values are found in the data.'}
                </Alert>
              ) : (
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {flagDefinitions.length} flag definition(s) configured
                  </Typography>

                  {flagDefinitions.map(flagDef => (
                    <FlagDefinitionCard
                      key={flagDef.id}
                      flagDefinition={flagDef}
                      onUpdate={handleUpdateFlag}
                      isEditing={editingId === flagDef.id}
                      onEditToggle={editing =>
                        setEditingId(editing ? flagDef.id : null)
                      }
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default FlagDictionaryManager;
